import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Award } from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * Per-product analytics for the signed-in ambassador.
 *
 * Pulls `/api/ambassador/me/product-analytics` which aggregates the
 * ambassador's paid orders grouped by product, then ranks bottles + revenue.
 * Helps the ambassador see WHICH fragrances convert best for them so they
 * can lean their storytelling and inventory toward the winners.
 */
export default function MyProductAnalytics() {
  const [data, setData] = useState({ items: [], total_bottles: 0, total_revenue: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/ambassador/me/product-analytics?limit=10`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setData(r.data || { items: [] }))
      .catch(() => setData({ items: [], total_bottles: 0, total_revenue: 0 }))
      .finally(() => setLoading(false));
  }, []);

  // Hide the section entirely until the ambassador has at least one sale —
  // empty leaderboards just feel discouraging. We surface it as soon as the
  // first paid order lands.
  if (!loading && (!data.items || data.items.length === 0)) return null;

  return (
    <section
      data-testid="my-product-analytics"
      className="mb-8"
    >
      <div className="flex items-end justify-between mb-3 border-b border-white/10 pb-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Your performance
          </p>
          <h2 className="text-xl text-white font-medium mt-0.5 font-sans">My Top-Converting Products</h2>
        </div>
        {data.items.length > 0 && (
          <div className="text-right">
            <p className="text-xs text-white/40 font-sans uppercase tracking-[0.15em]">Total</p>
            <p className="text-white text-sm font-sans">
              {data.total_bottles} bottles · ₱{Number(data.total_revenue).toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-6 text-white/40 text-xs font-sans">Loading…</div>
      ) : (
        <div className="space-y-1.5">
          {data.items.map((p, idx) => {
            const maxBottles = data.items[0]?.bottles || 1;
            const widthPct = Math.max(8, Math.round((p.bottles / maxBottles) * 100));
            return (
              <div
                key={p.product_id}
                className="bg-[#141414] border border-white/5 hover:border-[#D4AF37]/30 transition-colors p-3 flex items-center gap-3"
                data-testid={`my-prod-row-${p.product_id}`}
              >
                <div className="text-[#D4AF37] text-xs font-mono w-6 text-center flex-shrink-0">
                  {idx === 0 ? <Award className="w-4 h-4 inline" strokeWidth={1.8} /> : `#${idx + 1}`}
                </div>
                {p.image_url && (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    loading="lazy"
                    className="w-10 h-12 object-cover bg-[#0A0A0A] flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-sans truncate">{p.name}</p>
                  <div className="mt-1 relative h-1 bg-white/5 overflow-hidden">
                    <div
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB]"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="text-white text-sm font-sans font-medium">{p.bottles}</p>
                  <p className="text-white/40 text-[10px] uppercase tracking-[0.15em] font-sans">bottles</p>
                </div>
                <div className="text-right flex-shrink-0 hidden sm:block">
                  <p className="text-[#D4AF37] text-sm font-sans font-medium">₱{Number(p.revenue).toLocaleString()}</p>
                  <p className="text-white/40 text-[10px] uppercase tracking-[0.15em] font-sans">revenue</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
